import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  model,
  output,
  Output,
  Renderer2,
  signal,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  disabled,
  form,
  FormField,
  pattern,
  required,
} from "@angular/forms/signals";
import leaflet from "leaflet";
import "leaflet-draw"; // npm install --save-dev @types/leaflet-draw
import "@drustack/leaflet.resetview";
import {
  fromEvent,
  catchError,
  of,
  EMPTY,
  Observable,
  forkJoin,
  timer,
} from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { Client } from "../../models/client.model";
import { CommonModule } from "@angular/common";
import { revertStyle } from "./util/util.colorSmoothChange";
import { baseLayers } from "./util/utilMap";
import {
  clickStyle,
  featureStyle,
  getCropColor,
  getEtColor,
  getPrecipColor,
  hoverStyle,
  ownerStyle,
} from "./util/util.featureColors";
import { ModalService } from "../../services/modal/modal.service";
import {
  GeoCodingResults,
  GeocodingService,
  GeoCodingFeatures,
} from "../../services/api/geocoding/geocoding.service";
import {
  generateWaterYearDateList,
  generateWaterYearDateListPast,
  getDaysCountToNextMonth,
  getDaysCountToNextMonthPast,
  getDaysCountToPreviousMonth,
  getDaysCountToPreviousMonthPast,
} from "./util/utliWaterYear";
import moment from "moment";
import { AuthService } from "../../services/api/auth/auth.service";
import { DropdownComponent } from "../../dashboard/utils/dropdown/dropdown.component";
import { LoadingService } from "../../services/loading/loading";
import { DataService } from "../../services/api/data/data.service";
import { Role, ROLES } from "../../dashboard/utils/data-util";
import { SnackbarService } from "../../services/snackbar/snackbar.service";
import { deleteFieldUtil } from "../../utils/field-operations.util";
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  ApnCountySearchResult,
  FieldFeatureCollection,
} from "../../models/fields.model";

type CountyOption = { key: string; label: string };
interface AdvanceSearchForm {
  apn: string;
  county: string | null; // ! however its actually required
}
type GeoCodingSearchResult = GeoCodingFeatures & {
  isFieldResult?: boolean;
  isCoordinateResult?: boolean;
};

type CountyApnConfig = Record<
  string,
  {
    placeholder: string;
    regex: RegExp;
  }
>;

@Component({
  selector: "app-map",
  standalone: true,
  imports: [CommonModule, DropdownComponent, FormsModule, FormField],
  templateUrl: "./map.component.html",
  styleUrl: "./map.component.scss",
})
export class MapComponent {
  private map!: leaflet.Map;
  // Field Boundary GeoJsonLayer
  private geoJsonLayer!: leaflet.GeoJSON;
  // Layer of Current User Claimed Fields
  private claimedGeoJsonLayer?: leaflet.GeoJSON;
  private authService = inject(AuthService);
  private mapInitialized = false; // To make map lazy loading
  private resetViewControl?: any;
  private currentMode: string = "";
  private pendingClaimedStyleFrame?: number;
  // Labels (permanent tooltips) are only rendered for claimed fields that are
  // both zoomed-in past this level AND inside the current viewport. Without
  // this gating, large districts (e.g. East Kaweah, client 38, ~80k fields)
  // freeze for 1-2 minutes when labels are toggled on, because every field's
  // tooltip becomes a DOM node at once. Tune LABEL_MIN_ZOOM to taste.
  private readonly LABEL_MIN_ZOOM = 14;
  // Number of fields processed per animation frame when (un)binding labels.
  // Spreading the DOM work across frames avoids a long synchronous freeze.
  private readonly LABEL_RENDER_CHUNK = 200;
  private readonly labeledLayers = new Set<any>();
  private labelRefreshTimer?: ReturnType<typeof setTimeout>;
  private labelRenderHandle?: number;
  // Current map zoom, tracked so the template can show a hint when labels are
  // toggled on but the user is zoomed too far out for them to render.
  private readonly currentZoom = signal<number>(0);
  // Stable reference so the map pan/zoom listener can be cleaned up if needed.
  private readonly handleMapViewChange = () => {
    this.currentZoom.set(this.map.getZoom());
    this.scheduleLabelRefresh();
  };
  private hasInteractedWithTimeSlider = false;
  private readonly managerEtDateLoadingToken = "manager-map-et-date-loading";
  private pendingEtDateRequest?: string;
  pendingClaimProperties?: string;
  private readonly claimSessionKeyPrefix = "claimDisclaimerSessionSeen:";
  private destroyRef = inject(DestroyRef);

  @Input() fields?: FieldFeatureCollection;
  @Input({ required: true }) clients!: Client[];
  @Input() isUpdateZoomLevel?: boolean;
  @Input() etHistoryLoading = false;
  @Input() resolvedEtDates: string[] = [];
  @Input() failedEtDates: string[] = [];
  @Input() initialSymbology: "et" | "precipitation" | "crop" = "et";
  @Output() featureProperties = new EventEmitter<string>();
  @Output() etDateRequested = new EventEmitter<string>();
  fieldOperatedEvent = output<void>();
  @Input() type!: Role;

  @ViewChild("searchInput") searchInput?: ElementRef;
  @ViewChild("searchContainer") searchContainer?: ElementRef;
  @ViewChild("layerControlPanelContainer")
  layerControlPanelContainer?: ElementRef;
  @ViewChild("legendPanelContainer") legendPanelContainer?: ElementRef;
  @ViewChild("legendPanelMobileContainer")
  legendPanelMobileContainer?: ElementRef;
  @ViewChild("timeSliderMobileContainer")
  timeSliderMobileContainer?: ElementRef;
  @ViewChild("layerControlPanelMobileContainer")
  layerControlPanelMobileContainer?: ElementRef;
  @ViewChild("advancedSearchContainer")
  advancedSearchContainer?: ElementRef<HTMLDivElement>;
  searchResults?: GeoCodingSearchResult[];

  // TimeSlider DateList
  dateListTimeSlider = generateWaterYearDateList();
  // TimeSlider Date
  minDate: number = 0;
  maxDate: number = this.dateListTimeSlider.length - 1;
  dateTimeSlider: number = this.maxDate;
  TimeSliderLableList = [
    this.dateListTimeSlider[0],
    this.dateListTimeSlider[this.dateListTimeSlider.length - 1],
  ];
  // Set the Initialized timeSlider Paly condition
  isPlay: boolean = false;
  // Define the timeInterval to control the movement of time slider
  timeInterval: any;
  // Define Layer Display Dictionary (object)
  displayLayerList: { [key: string]: boolean } = {
    claimed_fields: true,
    not_claimed_fields: true,
  };
  // Symbology
  curSymbology = "et";
  // Label Status
  showLabel: boolean = false;
  // Crop Legend List
  cropLegend: any = [];

  // Mobile Items display
  displayMobileItems: { [key: string]: boolean } = {
    layerControlMobile: true,
    timeSliderMobile: false,
    legendMobile: false,
  };

  isHovered = false;
  popoverTimeout: any; // prevent the popover to close right away, causing jittering for user
  selectedClientId = model.required<string>(); // ? need to have default value passed in from parent

  readonly onMouseEnter = () => {
    clearTimeout(this.popoverTimeout);
    this.isHovered = true;
  };

  readonly onMouseLeave = () => {
    this.popoverTimeout = setTimeout(() => {
      this.isHovered = false;
    }, 200);
  };

  // Advanced Search
  searching = signal<boolean>(false);
  countiesForSearchOptions = rxResource<CountyOption[], number>({
    params: () => Number(this.selectedClientId()),
    stream: ({ params: clientId }) =>
      this.dataService
        .getCountiesForFieldSearch(clientId)
        .pipe(
          map((counties) =>
            counties.map((county) => ({ key: county, label: county })),
          ),
        ),
    defaultValue: [] as CountyOption[],
  });
  advancedSearchModel = signal<AdvanceSearchForm>({
    apn: "",
    county: null, // ! Can't hook up selection from selectedClientId yet, will do so soon
  });
  advancedSearchForms = form(this.advancedSearchModel, (schemaPath) => {
    required(schemaPath.apn, { message: "APN is required" });
    required(schemaPath.county, { message: "County is required" });
    pattern(
      schemaPath.apn,
      () =>
        this.getPlaceholderAndRegex(this.advancedSearchModel().county ?? "")
          .regex,
      { message: "APN format does not match the selected county's format" },
    );
    disabled(schemaPath.apn, () => this.searching());
    disabled(schemaPath.county, () => this.searching());
  });
  advancedSearchErrors = computed(() => {
    const errorList: string[] = [];
    if (
      this.advancedSearchForms.apn().touched() &&
      this.advancedSearchForms.apn().invalid()
    ) {
      this.advancedSearchForms
        .apn()
        .errors()
        .forEach((error) => {
          if (error.message) {
            errorList.push(error.message);
          }
        });
    }
    if (
      this.advancedSearchForms.county().touched() &&
      this.advancedSearchForms.county().invalid()
    ) {
      this.advancedSearchForms
        .county()
        .errors()
        .forEach((error) => {
          if (error.message) {
            errorList.push(error.message);
          }
        });
    }
    return errorList;
  });

  // Constructor
  constructor(
    private geocodingService: GeocodingService,
    private loadingService: LoadingService,
    private modalService: ModalService,
    private dataService: DataService,
    private snackbarService: SnackbarService,
    private renderer: Renderer2,
  ) {
    this.currentMode = this.authService.internalModeValue;
    if (this.currentMode === "demo") {
      this.dateListTimeSlider = generateWaterYearDateListPast();
      this.maxDate = this.dateListTimeSlider.length - 1;
      this.dateTimeSlider = this.maxDate;
      this.TimeSliderLableList = [
        this.dateListTimeSlider[0],
        this.dateListTimeSlider[this.dateListTimeSlider.length - 1],
      ];
    }

    // When the user edits the advanced search form, drop the previous
    // highlights so the map doesn't keep showing stale matches. The
    // `suppressHighlightClear` escape hatch lets callers reset the form
    // programmatically (e.g. after selecting a normal-search result)
    // without wiping the highlight they just painted.
    effect(() => {
      // Track both form values so the effect re-runs on either change.
      this.advancedSearchForms.apn().value();
      this.advancedSearchForms.county().value();
      if (this.suppressHighlightClear) {
        this.suppressHighlightClear = false;
        return;
      }
      this.clearHighlightedFields();
    });

    // Close the advanced search panel when clicking outside
    effect((onCleanup) => {
      if (this.searching()) return;
      const clickListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (
          this.advancedSearchContainer?.nativeElement.contains(target) ||
          this.searchContainer?.nativeElement.contains(target)
        ) {
          return;
        }
      };
      const unlisten = this.renderer.listen("document", "click", clickListener);
      onCleanup(() => unlisten());
    });

    // Whenever the counties list refreshes (e.g. selectedClientId changes),
    // reset the county selection to the first available option.
    effect(() => {
      if (
        !this.countiesForSearchOptions.hasValue() ||
        this.countiesForSearchOptions.value().length === 0
      )
        return;

      this.advancedSearchModel.update((model) => ({
        ...model,
        county: this.countiesForSearchOptions.value()[0].key,
      }));
    });

    // When the selected client changes, clear both search inputs so the
    // user starts from a clean state.
    effect(() => {
      this.selectedClientId();
      if (this.searchInput) {
        this.searchInput.nativeElement.value = "";
      }
      this.searchResults = [];
      this.advancedSearchForms.apn().reset("");
    });
  }

  onSubmitAdvancedSearch = () => {
    this.advancedSearchForms.apn().markAsTouched();
    this.advancedSearchForms.county().markAsTouched();
    if (this.advancedSearchErrors().length > 0) {
      return;
    }
    this.searching.set(true);

    const apn = this.advancedSearchForms.apn().value().trim().toUpperCase();
    const county = this.advancedSearchForms.county().value()!;

    forkJoin([
      this.dataService
        .searchFieldsByApnAndCounty(
          apn,
          county,
          Number(this.selectedClientId()),
        )
        .pipe(catchError(() => of({ uniqueid: [] } as ApnCountySearchResult))),
      timer(300),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.searching.set(false)),
      )
      .subscribe(([result]) => {
        if (result.uniqueid.length === 0) {
          this.snackbarService.show({
            message: `No fields found for APN "${apn}" in ${county} County.`,
          });
          return;
        }
        this.highlightAndZoomFields(result.uniqueid);
        // The advanced search now owns the map's highlight state; clear any
        // stale text/results from the regular search so only one search is
        // visibly "active" at a time.
        if (this.searchInput) {
          this.searchInput.nativeElement.value = "";
        }
        this.searchResults = [];
      });
  };

  /** toggle the Mobile Bars to change the visibility */
  toggleMobileItems(name: string) {
    for (const key in this.displayMobileItems) {
      this.displayMobileItems[key] =
        key === name ? !this.displayMobileItems[name] : false;
    }
    this.preventMapInteractions();
  }

  /** toggle the label visibility */
  toggleTooltips(event: any) {
    // Condition to update the label status
    this.showLabel = event.target.checked;
    // Only the labels need recomputing here (styles are unaffected), and the
    // viewport/zoom gating keeps this cheap even for very large districts.
    this.refreshVisibleLabels();
  }

  /**
   * Labels are suppressed below LABEL_MIN_ZOOM (see refreshVisibleLabels). When
   * the toggle is on but the user is still zoomed out that far, the checkbox
   * looks like it did nothing — this drives a small "zoom in to see labels"
   * hint so the user knows the labels will appear once they zoom in.
   */
  get labelZoomHintVisible(): boolean {
    return this.showLabel && this.currentZoom() < this.LABEL_MIN_ZOOM;
  }

  /** Function triggered when layer list control change (checkbox change) */
  updateLayerDisplay(event: any, layerName: string) {
    // Condition to update the display status
    this.displayLayerList[layerName] = event.target.checked;
    // Update the layer display.
    if (this.displayLayerList[layerName]) {
      // reopen & reorder layers
      //this.reOpenAndReorderLayers();
      if (layerName === "claimed_fields") {
        this.claimedGeoJsonLayer?.addTo(this.map);
        this.updateFieldsStyles();
      }
      if (layerName === "not_claimed_fields") {
        this.geoJsonLayer.addTo(this.map);
      }
    } else {
      // Hide the layer
      if (
        layerName === "not_claimed_fields" &&
        this.map.hasLayer(this.geoJsonLayer as leaflet.GeoJSON)
      ) {
        this.map.removeLayer(this.geoJsonLayer as leaflet.GeoJSON);
      }
      if (
        layerName === "claimed_fields" &&
        this.map.hasLayer(this.claimedGeoJsonLayer as leaflet.GeoJSON)
      ) {
        this.map.removeLayer(this.claimedGeoJsonLayer as leaflet.GeoJSON);
      }
    }
  }

  /** Function triggered when checked the radio */
  onSymbologyUpdate(event: any, value: string) {
    this.curSymbology = value;
    this.syncSelectedEtDateLoading();
    this.updateFieldsStyles();
  }

  /** Function for rerender the claimeFields Layer when the time slider moves or switching the symbology */
  updateFieldsStyles(): void {
    if (this.pendingClaimedStyleFrame) {
      cancelAnimationFrame(this.pendingClaimedStyleFrame);
    }
    this.pendingClaimedStyleFrame = requestAnimationFrame(() => {
      this.pendingClaimedStyleFrame = undefined;
      this.updateClaimedLayerPresentation();
    });
  }

  /** Time Slider Function: Trigger when updating Time Slider */
  updateTimeSlider(event: Event): void {
    // Update the dateTime of the time slider (update the index, can get the date value from date list)
    const input = event.target as HTMLInputElement;
    this.dateTimeSlider = Number(input.value);
    this.hasInteractedWithTimeSlider = true;
    this.syncSelectedEtDateLoading();
    this.updateFieldsStyles();
  }

  /** Time Slider Function: Trigger when click the button of move to next or previous day */
  moveTimeSliderDate(delta: number): void {
    this.dateTimeSlider += delta;
    // Move to the last record: current date
    if (this.dateTimeSlider > this.dateListTimeSlider.length - 1)
      this.dateTimeSlider = this.dateListTimeSlider.length - 1;
    // Move to the first record: Oct 1 Last Year
    if (this.dateTimeSlider < 0) this.dateTimeSlider = 0;
    this.hasInteractedWithTimeSlider = true;
    this.syncSelectedEtDateLoading();
    this.updateFieldsStyles();
  }

  /** Time Slider Function: Trigger when click the button of move to next or previous month */
  moveTimeSliderMonth(delta: number): void {
    // Get the current data string
    const currentDateStr = this.dateListTimeSlider[this.dateTimeSlider];
    if (delta === 1) {
      // Move to the next month
      const deltaNextDays =
        this.currentMode === "demo" ?
          getDaysCountToNextMonthPast(currentDateStr)
        : getDaysCountToNextMonth(currentDateStr);
      this.moveTimeSliderDate(deltaNextDays);
    } else if (delta === -1) {
      // Move to the previous month
      const deltaPrevDays =
        this.currentMode === "demo" ?
          getDaysCountToPreviousMonthPast(currentDateStr)
        : getDaysCountToPreviousMonth(currentDateStr);
      this.moveTimeSliderDate(-deltaPrevDays);
    }
  }

  /** Time Slider Function: Trigger when click the play button of the time slider */
  togglePlayTimeSlider(): void {
    this.isPlay = !this.isPlay;
    // Playing the Time Slider
    if (this.isPlay) {
      // Start mvoing the time slider every second
      this.timeInterval = setInterval(() => {
        if (this.dateTimeSlider < this.maxDate) {
          this.moveTimeSliderDate(1);
        } else {
          clearInterval(this.timeInterval);
          this.isPlay = false;
        }
      }, 1000);
    } else {
      // Stop the time slider
      clearInterval(this.timeInterval);
    }
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  private destroyMap(): void {
    this.loadingService.stop(this.managerEtDateLoadingToken);
    if (this.pendingClaimedStyleFrame) {
      cancelAnimationFrame(this.pendingClaimedStyleFrame);
      this.pendingClaimedStyleFrame = undefined;
    }
    if (this.labelRefreshTimer) {
      clearTimeout(this.labelRefreshTimer);
      this.labelRefreshTimer = undefined;
    }
    this.cancelLabelRender();
    if (this.map) {
      this.map.remove();
    }
  }

  /**
   * Search Input Binding Function
   * @param event
   */
  async onInputChange(event: Event): Promise<void> {
    const query = (event.target as HTMLInputElement).value;
    //
    if (!query) {
      this.removeMarker();
    }
  }

  private setupSearchSubscription(): void {
    fromEvent<Event>(this.searchInput?.nativeElement, "input")
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300), // Debounce to wait for a pause in typing
        map((event: Event) => (event.target as HTMLInputElement).value.trim()), // Trim whitespace
        distinctUntilChanged(), // Only proceed if the query has changed
        tap((query) => {
          // If the input is empty, immediately clear the results and return
          if (query === "") {
            this.searchResults = []; // Clear results immediately
          }
        }),
        switchMap((query): Observable<GeoCodingSearchResult[]> => {
          if (query === "") {
            return EMPTY; // Stop further processing if the input is empty
          }

          // Check if the query matches a field ID
          const matchingFields = this.fields?.features.filter((field) =>
            String(field.properties.id)
              .toLowerCase()
              .includes(query.toLowerCase()),
          );
          if (matchingFields && matchingFields.length > 0) {
            // Return field results - zoom to field bounds instead of calculating centroid
            return of(
              matchingFields.slice(0, 5).map<GeoCodingSearchResult>(
                (field) =>
                  ({
                    id: field.properties.id.toFixed(0),
                    type: "Feature",
                    place_type: ["field"],
                    relevance: 1,
                    properties: {},
                    text: String(field.properties.id),
                    place_name: `Land IQ Field ID: ${field.properties.id} - County: ${field.properties.county}`,
                    bbox: [],
                    center: [],
                    geometry: { type: "Point", coordinates: [] },
                    context: [],
                    isFieldResult: true,
                  }) satisfies GeoCodingSearchResult,
              ),
            );
          }

          // Check if the query is a lat/long coordinate
          if (this.geocodingService.isLatLongFormat(query)) {
            const coords = this.geocodingService.parseLatLong(query);
            if (coords) {
              // Use reverse geocoding for lat/long input
              return this.geocodingService
                .geocodeReverse(coords.lng, coords.lat)
                .pipe(
                  catchError(() =>
                    of({
                      features: [],
                      attribution: "",
                      query: [],
                      type: "",
                    } satisfies GeoCodingResults),
                  ),
                  map((results: GeoCodingResults): GeoCodingSearchResult[] => {
                    if (!results.features) return [];
                    // If no reverse geocode result, create a synthetic result for the coordinates
                    if (results.features.length === 0) {
                      return [
                        {
                          id: `coordinates.${coords.lng},${coords.lat}`,
                          type: "Feature",
                          place_type: ["coordinate"],
                          relevance: 1,
                          properties: {},
                          text: `${coords.lat}, ${coords.lng}`,
                          place_name: `Coordinates: ${coords.lat}, ${coords.lng}`,
                          bbox: [
                            coords.lng,
                            coords.lat,
                            coords.lng,
                            coords.lat,
                          ],
                          center: [coords.lng, coords.lat],
                          geometry: {
                            type: "Point",
                            coordinates: [coords.lng, coords.lat],
                          },
                          context: [],
                          isCoordinateResult: true,
                        },
                      ] satisfies GeoCodingSearchResult[];
                    }
                    return results.features;
                  }),
                );
            }
          }

          // Standard address geocoding
          return this.geocodingService.geocode(query).pipe(
            catchError(() =>
              of({
                features: [],
                attribution: "",
                query: [],
                type: "",
              } as GeoCodingResults),
            ),
            map((results): GeoCodingSearchResult[] => results?.features ?? []),
          );
        }),
      )
      .subscribe((results) => {
        // Type of search results
        this.searchResults = results; // Update the search results
      });
  }

  marker?: leaflet.Marker;

  private addMarker(lat: number, lng: number): void {
    this.removeMarker();
    this.marker = leaflet.marker([lat, lng]).addTo(this.map);
    this.map.setView([lat, lng], 11); // Adjust the map view to the marker's location
  }

  clearSearch(): void {
    this.removeMarker();
    if (this.searchInput) {
      this.searchInput.nativeElement.value = "";
      // Manually trigger the input event
      const event = new Event("input", { bubbles: true });
      this.searchInput.nativeElement.dispatchEvent(event);
      // Return focus to the input so the user can immediately type a new query.
      this.searchInput.nativeElement.focus();
    }
  }

  removeMarker(): void {
    if (this.marker) {
      this.map.removeLayer(this.marker); // Remove the previous marker if it exists
    }
  }

  selectLocation(location: GeoCodingSearchResult) {
    if (location?.isFieldResult && location?.id) {
      this.highlightAndZoomFields([Number(location.id)]);
    } else if (location?.center) {
      // Handle regular location result - add marker and pan
      const [lng, lat] = location.center;
      this.addMarker(lat, lng);
    }

    // Autofill the regular search input with what was selected:
    // - field result: the bare field id
    // - coordinate-only result: "lat, lng"
    // - address / geocoded result: the full place_name
    // If the relevant text is missing, leave the input as-is rather than
    // clobbering it with a fallback or an empty string.
    if (this.searchInput) {
      const fillText =
        location?.isFieldResult || location?.isCoordinateResult ?
          location.text
        : location?.place_name;
      if (fillText) this.searchInput.nativeElement.value = fillText;
    }

    this.searchResults = []; // Clear search results after selection

    // Reset the advanced search field so the two searches don't look
    // simultaneously active. Suppress the highlight-clear effect so the
    // field we just highlighted above stays highlighted.
    this.suppressHighlightClear = true;
    this.advancedSearchForms.apn().reset("");
  }

  /**
   * Highlight one or more fields by id (across both claimed and unclaimed
   * layers) and zoom the map to fit all matches. When exactly one field
   * matches, its popup is also opened.
   */
  private highlightedLayers: { layer: any; group: leaflet.GeoJSON }[] = [];
  private suppressHighlightClear = false;

  private highlightAndZoomFields(ids: number[]): void {
    // Always clear any previous highlight first so opening a new search
    // doesn't leave stale styled fields behind.
    this.clearHighlightedFields();

    if (ids.length === 0) return;

    const idSet = new Set<number>(ids);
    const matched: { layer: any; group: leaflet.GeoJSON }[] = [];
    // So we can reuse the group context
    const collect = (group: leaflet.GeoJSON) => (layer: any) => {
      if (idSet.has(layer.feature?.properties?.id)) {
        layer.setStyle(clickStyle);
        matched.push({ layer, group });
      }
    };
    if (this.geoJsonLayer)
      this.geoJsonLayer.eachLayer(collect(this.geoJsonLayer));
    if (this.claimedGeoJsonLayer)
      this.claimedGeoJsonLayer.eachLayer(collect(this.claimedGeoJsonLayer));

    if (matched.length === 0) return;
    this.highlightedLayers = matched;

    if (matched.length === 1) {
      matched[0].layer.openPopup?.();
    }

    const bounds = matched.reduce<leaflet.LatLngBounds | null>(
      (acc, { layer }) => {
        const b = layer.getBounds() as leaflet.LatLngBounds;
        return acc ? acc.extend(b) : b;
      },
      null,
    );
    if (bounds) {
      this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
    this.removeMarker();
  }

  private clearHighlightedFields(): void {
    for (const { layer, group } of this.highlightedLayers) {
      group.resetStyle(layer);
      layer.closePopup?.();
    }
    this.highlightedLayers = [];
  }

  ngOnInit(): void {
    // Define the global function
    (window as any).showSelectUpdateModal =
      this.showSelectUpdateModal.bind(this);
    (window as any).releaseField = this.releaseField.bind(this);
  }

  ngAfterViewInit(): void {
    // this.addGeoSearchControl();
    this.setupSearchSubscription();
    this.preventMapInteractions();
  }

  private preventMapInteractions(): void {
    const searchContainer = this.searchContainer?.nativeElement;
    const layerControlPanelContainer =
      this.layerControlPanelContainer?.nativeElement;
    const legendPanelContainer = this.legendPanelContainer?.nativeElement;
    const legendPanelMobileContainer =
      this.legendPanelMobileContainer?.nativeElement;
    const timeSliderMobileContainer =
      this.timeSliderMobileContainer?.nativeElement;
    const layerControlPanelMobileContainer =
      this.layerControlPanelMobileContainer?.nativeElement;

    ["dblclick", "mousedown", "touchstart", "wheel", "touchmove"].forEach(
      (eventType) => {
        searchContainer?.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
        layerControlPanelContainer.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
        legendPanelContainer.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
        legendPanelMobileContainer.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
        timeSliderMobileContainer.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
        layerControlPanelMobileContainer.addEventListener(
          eventType,
          (e: { stopPropagation: () => any }) => e.stopPropagation(),
        );
      },
    );
  }

  /**
   * Fields should be always fetched when app start (initialize or refresh), which cause the input changes,
   * so this hook could be a good place to initialize Map with Geojson (feilds) or update Geojson (fields) only.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialSymbology"]?.firstChange) {
      this.curSymbology = this.initialSymbology;
    }

    if (changes["fields"] && this.fields) {
      if (!this.mapInitialized) {
        this.initMapWithGeoJson();
      } else {
        // Update the Geojson Layer (Selection Change -> Fetch fields of selected client)
        if (
          this.type === "grower" ||
          this.type === "entity" ||
          this.type === "manager"
        ) {
          this.updateGeoJsonLayer(this.fields);
        }
      }

      // Generate Crop Legend List
      const cropSet = new Set();
      this.cropLegend = [];
      this.cropLegend.push({ crop: "No Crop Planting", color: "#fde047" });
      const claimedFields = this.fields?.features.filter(
        (field: any) => field.properties.owner === true,
      );
      claimedFields.forEach((field: any) => {
        [
          field.properties.claimed_details.crop_type_01,
          field.properties.claimed_details.crop_type_02,
          field.properties.claimed_details.crop_type_03,
        ]
          .filter((crop) => typeof crop === "string" && crop.trim())
          .forEach((crop) => cropSet.add(crop));
      });
      // Loop the set, build the cropLegend
      for (let crop of cropSet) {
        this.cropLegend.push({
          crop: crop,
          color: getCropColor(crop as string),
        });
      }
    }

    if (
      changes["fields"] ||
      changes["etHistoryLoading"] ||
      changes["resolvedEtDates"]
    ) {
      this.syncSelectedEtDateLoading();
    }
  }

  /**
   * Initilize the Leaflet Map with fields (SWSD as default for now)
   */
  private initMapWithGeoJson(): void {
    // Create the Field Boundary GeoJson Layer and get its bounds
    const fieldBoundaryLayer = this.createFieldsGeoJsonLayer(
      this.fields,
      "unclaimed",
    );
    // Create the Claimed Fields GeoJson Layer
    const claimedFieldsLayer = this.createFieldsGeoJsonLayer(
      this.fields,
      this.getClaimedMode(),
    );
    const bounds =
      fieldBoundaryLayer.getBounds().isValid() ?
        fieldBoundaryLayer.getBounds()
      : claimedFieldsLayer.getBounds();
    // Bounds is most likely valid if backend server runs well
    if (bounds.isValid()) {
      // Create the Map and set the bounds to the current client's fields bound
      this.map = leaflet.map("map").fitBounds(bounds);
      // ! Need to test with custom bounds so that the user will default to a different location
      this.map.doubleClickZoom.disable();
      // Set Base Map (BaseLayer) and Layer Control
      this.addBaseLayer();
      // Add the GeoJson Layer (fields) and Claimed GeoJson Layer to the map and set the local variable
      this.geoJsonLayer = fieldBoundaryLayer.addTo(this.map);
      this.claimedGeoJsonLayer = claimedFieldsLayer.addTo(this.map);
      this.claimedGeoJsonLayer.eachLayer((layer) => {
        layer.closeTooltip();
      });
      // Recompute which labels are visible whenever the user pans or zooms, so
      // we only ever keep the on-screen subset rendered.
      this.map.on("moveend zoomend", this.handleMapViewChange);
      this.currentZoom.set(this.map.getZoom());
      // Set mapInitialized to true
      this.mapInitialized = true;
      // Set Scale Control
      leaflet.control.scale({ position: "bottomleft" }).addTo(this.map);
      // Set reset view control
      this.resetViewControl = (leaflet.control as any)
        .resetView({
          position: "topleft",
          title: "Reset view",
          latlng: bounds.getCenter(),
          zoom: this.map.getBoundsZoom(bounds),
        })
        .addTo(this.map);
      // ! old leaflet plugin using href=# to reset view, which causes page jump to top - overwrite the behavior
      this.map.getContainer().addEventListener("click", (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(".leaflet-bar-part-single")) {
          e.preventDefault();
        }
      });
    } else {
      // If Backend Server failed, only initialize the map, just print the log for now
      this.initMapDefault();
    }
  }

  private queueDisclaimerAndShow(properties: string) {
    this.pendingClaimProperties = properties;
    this.modalService.openDialogAsync({
      title: "Before You Claim",
      message: `Please only claim fields that you own. If this is not your field, do not proceed.`,
      actions: [
        {
          label: "I Understand",
          action: () => {
            this.onConfirmClaimDisclaimer();
            return "confirm";
          },
        },
        {
          label: "Cancel",
          action: () => {
            this.onCancelClaimDisclaimer();
            return "cancel";
          },
        },
      ],
    });
  }

  private getUserId(): string | null {
    try {
      return this.authService.getUserId();
    } catch {
      return null;
    }
  }

  private shouldShowClaimDisclaimer(): boolean {
    // Only show disclaimer for grower users and when not in demo mode
    if (
      this.type !== "grower" ||
      this.authService.internalModeValue === "demo"
    ) {
      return false;
    }

    const userId = this.getUserId() ?? "unknown";
    const token = this.authService.getToken?.();
    const sessionKey = `${this.claimSessionKeyPrefix}${userId}:${token?.slice(-10) ?? "no-token"}`;
    const seenThisSession = sessionStorage.getItem(sessionKey) === "true";

    return !seenThisSession;
  }

  onConfirmClaimDisclaimer() {
    if (this.pendingClaimProperties) {
      this.map.closePopup();
      this.featureProperties.emit(this.pendingClaimProperties);
      this.pendingClaimProperties = undefined;
    }
    const userId = this.getUserId() ?? "unknown";
    const token = this.authService.getToken?.();
    const sessionKey = `${this.claimSessionKeyPrefix}${userId}:${token?.slice(-10) ?? "no-token"}`;
    sessionStorage.setItem(sessionKey, "true");
  }

  onCancelClaimDisclaimer() {
    this.pendingClaimProperties = undefined;
  }

  /**
   * <h3 class="text-xl font-semibold">id: ${properties.id}</h3>
   * <p class="text-sm">client_id: ${properties.client_id}</p>
   * @param properties
   * @returns
   */
  private createPopupContent(properties: any): string {
    let propertiesWithFormat = JSON.stringify(properties);
    propertiesWithFormat = propertiesWithFormat
      .replace(/"/g, "&quot;") // Replace double quotes with HTML-safe entity
      .replace(/'/g, "\\'"); // Replace single quotes with HTML-safe entity
    if (this.type === "grower") {
      return `
      <div class="h-full w-full pt-1 pl-1 pr-1 pb-3 bg-gray-50 rounded-lg shadow-lg dark:text-gray-300 dark:bg-gray-800 dark:shadow-gray-700">
      <p class="text-md font-semibold px-3">Land IQ Field ID: ${properties.id}</p>
      ${
        properties.owner ?
          `
          <p class="text-md font-semibold px-3">Field Name: ${properties?.claimed_details?.field_name}</p>
            <p class="text-md font-semibold px-3">Target Water: ${properties?.claimed_details?.total_target_water} ${properties?.claimed_details?.total_target_water_unit} (${properties?.claimed_details?.total_target_type})</p>
      `
        : ``
      }
        <p class="text-md font-semibold px-3">County: ${properties.county}</p>
        <p class="text-md font-semibold px-3">Acres: ${Number(properties.acres)
          .toFixed(1)
          .toString()}</p>
        <p class="text-md font-semibold px-3">District: ${properties.client_name}</p>
        <button class="mt-1 ml-2 px-2.5 pt-2 pb-1.5 bg-blue-500 text-white rounded hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 " onclick="showSelectUpdateModal('${propertiesWithFormat}')">${
          properties.owner || this.authService.internalModeValue === "demo" ?
            "VIEW"
          : "ADD"
        }</button>
      </div>
    `;
    } else if (this.type === "manager") {
      const roleLable =
        properties?.claimed_details?.role === "MANAGER" ? "Manager" : "Grower";
      return `
      <div class="h-full w-full pt-1 pl-1 pr-1 pb-3 bg-gray-50 rounded-lg shadow-lg dark:text-gray-300 dark:bg-gray-800 dark:shadow-gray-700">
      ${
        properties.owner ?
          `
          <p class="text-md font-semibold px-3">${roleLable} Name: ${properties?.claimed_details?.username}</p>
          <p class="text-md font-semibold px-3">${roleLable} Email: ${properties?.claimed_details?.email}</p>`
        : ``
      }
        <p class="text-md font-semibold px-3">Land IQ Field ID: ${properties.id}</p>
        ${
          properties.owner ?
            `
        <p class="text-md font-semibold px-3">Field Name: ${properties?.claimed_details?.field_name}</p>
        <p class="text-md font-semibold px-3">Target Water: ${properties?.claimed_details?.total_target_water} ${properties?.claimed_details?.total_target_water_unit} (${properties?.claimed_details?.total_target_type})</p>
          `
          : ``
        }
        <p class="text-md font-semibold px-3">County: ${properties.county}</p>
        <p class="text-md font-semibold px-3">Acres: ${Number(properties.acres)
          .toFixed(1)
          .toString()}</p>
          ${
            properties.owner ?
              `
        <div class="flex flex-row justify-between gap-2 mt-1 px-2">
          <button class="px-2.5 pt-2 pb-1.5 bg-blue-500 text-white rounded hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700" onclick="showSelectUpdateModal('${propertiesWithFormat}')">Show Details</button>
            ${
              this.authService.internalModeValue !== "demo" ?
                `<button class="px-2.5 pt-2 pb-1.5 bg-red-500 text-white rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700" onclick="releaseField('${propertiesWithFormat}')">Release</button>`
              : ""
            }`
            : this.authService.internalModeValue !== "demo" ?
              `<button class="mt-1 ml-2 px-2.5 pt-2 pb-1.5 bg-blue-500 text-white rounded hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 " onclick="showSelectUpdateModal('${propertiesWithFormat}')">ADD</button>`
            : ""
          }
        </div>
      </div>`;
    } else if (this.type === "entity") {
      return `
      <div class="h-full w-full pt-1 pl-1 pr-1 pb-3 bg-gray-50 rounded-lg shadow-lg dark:text-gray-300 dark:bg-gray-800 dark:shadow-gray-700">
      <p class="text-md font-semibold px-3">Land IQ Field ID: ${properties.id}</p>
      ${
        properties.owner ?
          `
          <p class="text-md font-semibold px-3">Field Name: ${properties?.claimed_details?.field_name}</p>
            <p class="text-md font-semibold px-3">Target Water: ${properties?.claimed_details?.total_target_water} ${properties?.claimed_details?.total_target_water_unit} (${properties?.claimed_details?.total_target_type})</p>
        `
        : ``
      }
        <p class="text-md font-semibold px-3">County: ${properties.county}</p>
        <p class="text-md font-semibold px-3">Acres: ${Number(properties.acres)
          .toFixed(1)
          .toString()}</p>
        <p class="text-md font-semibold px-3">District: ${properties.client_name}</p>
        ${
          properties.owner ?
            `
          <button class="mt-1 ml-2 px-2.5 pt-2 pb-1.5 bg-blue-500 text-white rounded hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700" onclick="showSelectUpdateModal('${propertiesWithFormat}')">VIEW</button>
        `
          : ``
        }
      </div>
    `;
    } else {
      return "";
    }
  }

  async releaseField(properties: string): Promise<void> {
    const parsed = JSON.parse(properties);
    const fieldId = parsed?.id;
    const fieldName = parsed?.claimed_details?.field_name || `Field ${fieldId}`;

    if (!fieldId) {
      return;
    }

    await deleteFieldUtil(
      fieldId,
      fieldName,
      "Release",
      {
        dataService: this.dataService,
        loadingService: this.loadingService,
        modalService: this.modalService,
        snackbarService: this.snackbarService,
      },
      () => {
        // Trigger parent to refetch fields
        this.fieldOperatedEvent.emit();
        this.map.closePopup();
      },
    );
  }

  showSelectUpdateModal(properties: string): void {
    const parsed = JSON.parse(properties);
    const claimedByCurrentUser = parsed?.owner === true;
    const fieldId = parsed?.id;

    // Check field claim status before proceeding for unclaimed fields <only for grower>
    if (
      !claimedByCurrentUser &&
      fieldId &&
      this.type === ROLES.GROWER &&
      this.authService.internalModeValue !== "demo"
    ) {
      this.loadingService.start({
        token: "check-field-claim-status",
        message: "Checking field status...",
      }); // ! Check this heavily since we have an async op inside the next
      this.dataService
        .checkFieldClaimStatus(fieldId)
        .pipe(
          finalize(() => {
            this.loadingService.stop("check-field-claim-status");
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (status) => {
            // Check the emailValidation firstly
            if (!status.emailValidation) {
              this.modalService.openDialogAsync({
                title: "No authorization to claim this field",
                message: status.message,
              });
              // Field already claimed by another user, do not proceed
              return;
            }
            // Then check the claimed conflict
            if (status.isClaimed) {
              this.modalService.openDialogAsync({
                title: "Field Already Claimed",
                message: status.message,
              });
              // Field already claimed by another user, do not proceed
              return;
            }
            this.proceedWithClaim(properties, claimedByCurrentUser);
          },
          error: () => {
            this.modalService.openDialogAsync({
              title: "Error",
              message:
                "Failed to check field claim status. Please try again later.",
            });
          },
        });
      return;
    }

    this.proceedWithClaim(properties, claimedByCurrentUser);
  }

  private proceedWithClaim(
    properties: string,
    claimedByCurrentUser: boolean,
  ): void {
    if (!claimedByCurrentUser && this.shouldShowClaimDisclaimer()) {
      this.queueDisclaimerAndShow(properties);
      return;
    }

    this.map.closePopup();
    this.featureProperties.emit(properties);
  }

  private hoveredLayer: any = null;
  private hoveredLayerGroup: leaflet.GeoJSON | null = null;
  private mapLeaveBound = false;

  private getClaimedMode(): string {
    switch (this.curSymbology) {
      case "precipitation":
        return "claimed_precip";
      case "crop":
        return "claimed_crop";
      default:
        return "claimed_et";
    }
  }

  private getFeatureEtValue(feature: any, key: "eta" | "prcp"): number | null {
    const row =
      feature?.properties?.claimed_details?.userFieldEtvalues?.[
        this.dateListTimeSlider[this.dateTimeSlider]
      ];
    const value = row?.[key];
    return typeof value === "number" ? value : null;
  }

  private isEtSymbologySelected(): boolean {
    return this.curSymbology === "et" || this.curSymbology === "precipitation";
  }

  private syncSelectedEtDateLoading(): void {
    const selectedDate = this.dateListTimeSlider[this.dateTimeSlider];
    const shouldShowLoading =
      this.type === "manager" &&
      this.isEtSymbologySelected() &&
      !!selectedDate &&
      !this.resolvedEtDates.includes(selectedDate) &&
      !this.isEtDateLoaded(selectedDate);

    if (shouldShowLoading) {
      this.loadingService.start({
        token: this.managerEtDateLoadingToken,
        message: "Loading ET data",
      });
      if (
        !this.etHistoryLoading &&
        this.pendingEtDateRequest !== selectedDate
      ) {
        this.pendingEtDateRequest = selectedDate;
        queueMicrotask(() => {
          if (this.pendingEtDateRequest !== selectedDate) {
            return;
          }
          this.pendingEtDateRequest = undefined;
          if (
            !this.resolvedEtDates.includes(selectedDate) &&
            !this.isEtDateLoaded(selectedDate)
          ) {
            this.etDateRequested.emit(selectedDate);
          }
        });
      }
    } else {
      this.loadingService.stop(this.managerEtDateLoadingToken);
    }
  }

  private isEtDateLoaded(date: string): boolean {
    const features = this.fields?.features ?? [];
    const claimedFeatures = features.filter(
      (field: any) => field.properties.owner === true,
    );

    if (
      this.type === "manager" &&
      features.length > 0 &&
      claimedFeatures.length === 0
    ) {
      return true;
    }

    for (const field of claimedFeatures) {
      if (
        // @ts-expect-error
        field.properties.claimed_details?.userFieldEtvalues?.[date]
      ) {
        return true;
      }
    }

    return false;
  }

  get isSelectedEtDateFailed(): boolean {
    const date = this.dateListTimeSlider[this.dateTimeSlider];
    return (
      this.isEtSymbologySelected() &&
      !this.etHistoryLoading &&
      this.failedEtDates.includes(date)
    );
  }

  get isSelectedEtDateUnavailable(): boolean {
    if (this.isSelectedEtDateFailed) return false;
    const date = this.dateListTimeSlider[this.dateTimeSlider];
    if (!date || !this.isEtSymbologySelected() || this.etHistoryLoading) {
      return false;
    }
    if (this.type === "manager" && !this.resolvedEtDates.includes(date)) {
      return false;
    }
    const claimedFields =
      this.fields?.features.filter(
        (field: any) => field.properties.owner === true,
      ) ?? [];
    return (
      claimedFields.length > 0 &&
      !claimedFields.some((field: any) => {
        const row = field.properties.claimed_details?.userFieldEtvalues?.[date];
        const value =
          row?.[this.curSymbology === "precipitation" ? "prcp" : "eta"];
        return typeof value === "number" && Number.isFinite(value);
      })
    );
  }

  private getFieldFillColor(feature: any, mode: string): string {
    switch (mode) {
      case "unclaimed":
        return featureStyle.fillColor;
      case "claimed_et": {
        const eta = this.getFeatureEtValue(feature, "eta");
        return eta == null ? ownerStyle.fillColor : getEtColor(eta);
      }
      case "claimed_precip": {
        const prcp = this.getFeatureEtValue(feature, "prcp");
        return prcp == null ? ownerStyle.fillColor : getPrecipColor(prcp);
      }
      case "claimed_crop":
        return this.cropColorOrType(feature, true);
      default:
        return featureStyle.fillColor;
    }
  }

  private getFieldTooltipText(feature: any, mode: string): string | null {
    switch (mode) {
      case "claimed_et": {
        const eta = this.getFeatureEtValue(feature, "eta");
        return eta == null ? null : eta.toFixed(2);
      }
      case "claimed_precip": {
        const prcp = this.getFeatureEtValue(feature, "prcp");
        return prcp == null ? null : prcp.toFixed(2);
      }
      case "claimed_crop":
        return this.cropColorOrType(feature, false);
      default:
        return null;
    }
  }

  private getFieldStyle(feature: any, mode: string) {
    const base = mode === "unclaimed" ? featureStyle : ownerStyle;
    return {
      fillColor: this.getFieldFillColor(feature, mode),
      fillOpacity: base.fillOpacity,
      color: base.color,
      opacity: base.opacity,
      weight: base.weight,
    };
  }

  private updateClaimedLayerPresentation(): void {
    if (!this.claimedGeoJsonLayer) return;
    const mode = this.getClaimedMode();
    // Restyle every claimed field — fill colors depend on the active symbology
    // and the time slider, so this always runs across the whole layer.
    this.claimedGeoJsonLayer.eachLayer((layer: any) => {
      const feature = layer.feature;
      if (!feature) return;
      layer.setStyle(this.getFieldStyle(feature, mode));
    });
    // Labels are handled separately so we only ever render the on-screen,
    // zoomed-in subset instead of one tooltip per field.
    this.refreshVisibleLabels();
  }

  /**
   * Render permanent labels (tooltips) only for claimed fields that are inside
   * the current viewport, and only once zoomed past LABEL_MIN_ZOOM. Off-screen
   * or zoomed-out tooltips are unbound entirely so they don't accumulate as DOM
   * nodes — this is what keeps very large districts (~80k fields) responsive.
   */
  private refreshVisibleLabels(): void {
    if (!this.claimedGeoJsonLayer || !this.map) return;

    // Cancel any chunked render still in flight from a previous pan/zoom so we
    // don't run two reconciliations against the same layers at once.
    this.cancelLabelRender();

    const showLabels =
      this.showLabel && this.map.getZoom() >= this.LABEL_MIN_ZOOM;
    if (!showLabels) {
      this.clearLabelsChunked();
      return;
    }

    const mode = this.getClaimedMode();
    const viewBounds = this.map.getBounds();
    const layers = this.claimedGeoJsonLayer.getLayers() as any[];

    // Bind/unbind labels in chunks across animation frames. Doing it all in one
    // synchronous pass is what froze the map for 1-3s when crossing the zoom
    // threshold in a dense district; spreading it keeps the map interactive
    // while the labels stream in.
    let i = 0;
    const step = () => {
      const end = Math.min(i + this.LABEL_RENDER_CHUNK, layers.length);
      for (; i < end; i++) {
        const layer = layers[i];
        const feature = layer.feature;
        if (!feature) continue;

        const inView =
          typeof layer.getBounds === "function" &&
          viewBounds.intersects(layer.getBounds());
        const tooltipText =
          inView ? this.getFieldTooltipText(feature, mode) : null;

        if (tooltipText != null) {
          const tooltip = layer.getTooltip?.();
          if (tooltip) {
            // Already shown — only touch the DOM if the value actually changed
            // (e.g. time slider moved). Avoids needless reflow while panning.
            if (tooltip.getContent() !== tooltipText) {
              tooltip.setContent(tooltipText);
            }
          } else {
            layer.bindTooltip(tooltipText, {
              permanent: true,
              direction: "center",
            });
            layer.openTooltip();
          }
          this.labeledLayers.add(layer);
        } else if (layer.getTooltip?.()) {
          layer.unbindTooltip();
          this.labeledLayers.delete(layer);
        }
      }
      if (i < layers.length) {
        this.labelRenderHandle = requestAnimationFrame(step);
      } else {
        this.labelRenderHandle = undefined;
      }
    };
    this.labelRenderHandle = requestAnimationFrame(step);
  }

  /** Remove currently-rendered labels in chunks (only walks the shown ones). */
  private clearLabelsChunked(): void {
    if (this.labeledLayers.size === 0) return;
    const layers = Array.from(this.labeledLayers);
    let i = 0;
    const step = () => {
      const end = Math.min(i + this.LABEL_RENDER_CHUNK, layers.length);
      for (; i < end; i++) {
        const layer = layers[i];
        if (layer.getTooltip?.()) layer.unbindTooltip();
        this.labeledLayers.delete(layer);
      }
      if (i < layers.length) {
        this.labelRenderHandle = requestAnimationFrame(step);
      } else {
        this.labelRenderHandle = undefined;
      }
    };
    this.labelRenderHandle = requestAnimationFrame(step);
  }

  private cancelLabelRender(): void {
    if (this.labelRenderHandle) {
      cancelAnimationFrame(this.labelRenderHandle);
      this.labelRenderHandle = undefined;
    }
  }

  /** Debounce label recomputation so it runs once per pan/zoom gesture. */
  private scheduleLabelRefresh(): void {
    if (this.labelRefreshTimer) clearTimeout(this.labelRefreshTimer);
    this.labelRefreshTimer = setTimeout(() => {
      this.labelRefreshTimer = undefined;
      this.refreshVisibleLabels();
    }, 150);
  }

  private createFieldsGeoJsonLayer(fields: any, mode: string): leaflet.GeoJSON {
    // 1) Pick features (claimed/unclaimed)
    const allFeatures = fields?.features ?? this.fields?.features ?? [];
    const filtered =
      mode === "unclaimed" ?
        allFeatures.filter((f: any) => f.properties.owner === false)
      : allFeatures.filter((f: any) => f.properties.owner === true);
    const style = (feature: any) => this.getFieldStyle(feature, mode);
    // 5) Create GeoJSON group
    const layerGroup = leaflet.geoJSON(filtered, {
      style,
      onEachFeature: (feature, layer) => {
        // popup always
        if (feature?.properties) {
          layer.bindPopup(this.createPopupContent(feature.properties));
        }
        // Labels are intentionally NOT bound here. They are managed centrally
        // by refreshVisibleLabels() so only on-screen, zoomed-in fields ever
        // get a tooltip — binding one per field at creation/re-add time is what
        // froze large districts when labels were toggled on.
        layer.on({
          mouseover: (e: any) => {
            const target = e.target;
            // Cross-layer hover fix: reset using the owning group
            if (
              this.hoveredLayer &&
              this.hoveredLayer !== target &&
              this.hoveredLayerGroup
            ) {
              this.hoveredLayerGroup.resetStyle(this.hoveredLayer);
            }
            this.hoveredLayer = target;
            this.hoveredLayerGroup = layerGroup;
            target.setStyle(hoverStyle);
            // Optional: bringToFront can hurt perf when many polygons/layers overlap
            if (
              !leaflet.Browser.ie &&
              !leaflet.Browser.opera &&
              !leaflet.Browser.edge
            ) {
              target.bringToFront();
            }
          },
          mouseout: (e: any) => {
            layerGroup.resetStyle(e.target);
            if (this.hoveredLayer === e.target) {
              this.hoveredLayer = null;
              this.hoveredLayerGroup = null;
            }
          },
          click: () => {
            const originalStyle =
              mode === "unclaimed" ?
                this.getFieldStyle(feature, "unclaimed")
              : this.getFieldStyle(feature, this.getClaimedMode());
            revertStyle(layer, clickStyle, originalStyle, 1200);
          },
        });
      },
    });
    // 6) One-time map leave safety (works across all layers)
    if (this.map && !this.mapLeaveBound) {
      this.mapLeaveBound = true;
      this.map.getContainer().addEventListener(
        "mouseleave",
        () => {
          if (this.hoveredLayer && this.hoveredLayerGroup) {
            this.hoveredLayerGroup.resetStyle(this.hoveredLayer);
          }
          this.hoveredLayer = null;
          this.hoveredLayerGroup = null;
        },
        { passive: true },
      );
    }
    return layerGroup;
  }

  cropColorOrType(feature: any, isColor: boolean) {
    const curDate = this.dateListTimeSlider[this.dateTimeSlider];
    const s1 = feature.properties.claimed_details.ls_start_01;
    const e1 = feature.properties.claimed_details.ls_end_01;
    const s2 = feature.properties.claimed_details.ls_start_02;
    const e2 = feature.properties.claimed_details.ls_end_02;
    const s3 = feature.properties.claimed_details.ls_start_03;
    const e3 = feature.properties.claimed_details.ls_end_03;

    if (
      s1 &&
      e1 &&
      moment.utc(curDate).isBetween(moment.utc(s1), moment.utc(e1), null, "[]")
    ) {
      return isColor ?
          getCropColor(feature.properties.claimed_details.crop_type_01)
        : feature.properties.claimed_details.crop_type_01;
    } else if (
      s2 &&
      e2 &&
      moment.utc(curDate).isBetween(moment.utc(s2), moment.utc(e2), null, "[]")
    ) {
      return isColor ?
          getCropColor(feature.properties.claimed_details.crop_type_02)
        : feature.properties.claimed_details.crop_type_02;
    } else if (
      s3 &&
      e3 &&
      moment.utc(curDate).isBetween(moment.utc(s3), moment.utc(e3), null, "[]")
    ) {
      return isColor ?
          getCropColor(feature.properties.claimed_details.crop_type_03)
        : feature.properties.claimed_details.crop_type_03;
    } else {
      return isColor ? "#fde047" : "No Crop Planting";
    }
  }

  /**
   * Updating the GeoJson Layer when new fields comming to the map component from app component,
   * As most style and other settings have been done the createGeoJsonLayer function, just need to
   * clear the layer and add the data.
   * @param fields
   */
  private updateGeoJsonLayer(fields: any): void {
    if (this.geoJsonLayer) {
      // Clear layer then add the new fields based on selected client
      this.geoJsonLayer.clearLayers();
      const notClaimedFields = fields?.features.filter(
        (field: any) => field.properties.owner === false,
      );
      this.geoJsonLayer.addData(notClaimedFields);
      // Clear Claimed Layer then filter finally add the new filtered fields
      const claimedFields = fields?.features.filter(
        (field: any) => field.properties.owner === true,
      );
      this.claimedGeoJsonLayer?.clearLayers();
      this.claimedGeoJsonLayer?.addData(claimedFields);
      this.updateFieldsStyles();
      if (this.isUpdateZoomLevel) {
        // fly to the selected client's bound
        this.smoothFlyToBounds(this.geoJsonLayer.getBounds());
      }
    }
  }

  /**
   * This function could make the fly smooth relatively when changing the selection of clients
   * @param bounds
   */
  private smoothFlyToBounds(bounds: L.LatLngBounds): void {
    const currentBounds = this.map.getBounds();
    const distance = currentBounds.getCenter().distanceTo(bounds.getCenter());
    // Calculate duration based on distance (for example, 1.5 second per 50km)
    const duration = Math.min(Math.max(distance / 50000, 1.5), 3); // Minimum 1 second, maximum 5 seconds
    this.map.flyToBounds(bounds, { animate: true, duration: duration });

    // Update reset view control after map movement completes
    this.map.once("moveend", () => {
      this.updateResetViewControl(bounds);
    });
  }

  /**
   * Update the reset view control with new bounds
   * @param bounds
   */
  private updateResetViewControl(bounds: L.LatLngBounds): void {
    if (this.resetViewControl) {
      this.map.removeControl(this.resetViewControl);
    }
    this.resetViewControl = (leaflet.control as any)
      .resetView({
        position: "topleft",
        title: "Reset view",
        latlng: bounds.getCenter(),
        zoom: this.map.getBoundsZoom(bounds),
      })
      .addTo(this.map);
  }

  /**
   * Adding the Base Map (BaseLayer) to the current map
   */
  private addBaseLayer(): void {
    // Set Satellite Base Layer as default
    baseLayers["Satellite"].addTo(this.map);
    // Set up Layer Control
    leaflet.control
      .layers(baseLayers, undefined, { position: "bottomright" })
      .addTo(this.map);
  }
  /**
   * This function is less likely to trigger if backend server runs well
   */
  private initMapDefault(): void {
    this.map = leaflet.map("map", {
      // center: [38.1281955, -119.502463], // Default center if no valid bounds
      // zoom: 8,
      doubleClickZoom: false,
    });
    this.addBaseLayer();
    this.mapInitialized = true;
  }

  /**
   * Associate county with the expected APN regexp
   * @todo Will need to be update whenever we have new county
   */
  getPlaceholderAndRegex = (county: string | null) => {
    const countApnSearchPlaceholder: CountyApnConfig = {
      Fresno: {
        placeholder: "e.g. 12345678 or 12345678ST",
        regex: /^\d{8}(S[TU]?|[TU])?$/,
      },
      Kern: {
        placeholder: "e.g. 12345678",
        regex: /^\d{8}$/,
      },
      Kings: {
        placeholder: "e.g. 123-456-789",
        regex: /^\d{3}-\d{3}-\d{3}$/,
      },
      Merced: {
        placeholder: "e.g. 123-456-789",
        regex: /^\d{3}-\d{3}-\d{3}$/,
      },
      Stanislaus: {
        placeholder: "e.g. 12345678 or 123456789",
        regex: /^\d{8,9}$/,
      },
      Tulare: {
        placeholder: "e.g. 123-456-789",
        regex: /^\d{3}-\d{3}-\d{3}$/,
      },
    };
    if (county && county in countApnSearchPlaceholder) {
      return countApnSearchPlaceholder[county];
    }
    return {
      placeholder: "",
      regex: /.*/,
    } satisfies CountyApnConfig[string];
  };
}
